import { addDays, format } from "date-fns";
import React, { useState, useRef, useEffect } from "react";
import DateRanger from "../../components-react/DateRanger";
import moment from "moment";
import {
  CalenderIcon,
  ClearIcon,
  CloseIcon,
} from "../../../images/icons/icons";
import "../../../styles/reports-subscription/timesheet.css";

const DateRangerFilter = ({ startDate, endDate, onDateChange }) => {
  const initialRangeState = [
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: "selection",
    },
  ];

  const [rangeState, setRangeState] = useState(initialRangeState);
  const [openDateRanger, setOpenDateRanger] = useState(false);
  const [popupPosition, setPopupPosition] = useState({});
  const containerRef = useRef(null);

  const isMobile = () => window.innerWidth <= 768;

  const calculatePosition = () => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const threshold = windowWidth / 2;

    // Mobile specific positioning
    if (isMobile()) {
      return {
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        opacity: 1,
        maxWidth: "90vw",
        maxHeight: "90vh",
        overflow: "auto",
        backgroundColor: "#fff",
        borderRadius: "8px",
        padding: "16px",
        margin: "auto",
      };
    }

    // Desktop positioning - using viewport-relative coordinates
    const dateRangerHeight = 400; // Approximate height of date ranger
    const dateRangerWidth = 320; // Approximate width of date ranger
    const gap = 8; // Gap between trigger and popup

    // Calculate vertical position
    let topPosition = rect.bottom + gap;

    // If popup would go below viewport, position it above the trigger
    if (topPosition + dateRangerHeight > windowHeight) {
      topPosition = rect.top - dateRangerHeight - gap;

      // If still doesn't fit above, center it in viewport
      if (topPosition < 0) {
        topPosition = Math.max(10, (windowHeight - dateRangerHeight) / 2);
      }
    }

    // Calculate horizontal position
    if (rect.left < threshold) {
      // Position to the left side
      const leftPosition = Math.max(
        10,
        Math.min(rect.left, windowWidth - dateRangerWidth - 10)
      );
      return {
        position: "fixed",
        top: `${topPosition}px`,
        left: `${leftPosition}px`,
        opacity: 1,
        transform: "translateY(0)",
        zIndex: 999999,
      };
    } else {
      // Position to the right side
      const rightPosition = Math.max(10, windowWidth - rect.right);
      return {
        position: "fixed",
        top: `${topPosition}px`,
        right: `${rightPosition}px`,
        opacity: 1,
        transform: "translateY(0)",
        zIndex: 999999,
      };
    }
  };

  const handleOpenDateRanger = () => {
    const position = calculatePosition();
    if (position) {
      if (isMobile()) {
        setPopupPosition(position);
      } else {
        setPopupPosition({
          ...position,
          opacity: 0,
          transform: "translateY(-10px)",
        });
      }
      setOpenDateRanger(true);

      if (!isMobile()) {
        requestAnimationFrame(() => {
          setPopupPosition(position);
        });
      }
    }
  };

  useEffect(() => {
    if (openDateRanger) {
      const handleScroll = () => {
        if (!isMobile()) {
          const newPosition = calculatePosition();
          if (newPosition) {
            setPopupPosition(newPosition);
          }
        }
      };

      const handleResize = () => {
        const newPosition = calculatePosition();
        if (newPosition) {
          setPopupPosition(newPosition);
        }
      };

      // Find all scrollable parent containers
      const getScrollableParents = (element) => {
        const scrollableParents = [];
        let parent = element.parentElement;

        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent);
          if (
            style.overflow === "scroll" ||
            style.overflow === "auto" ||
            style.overflowY === "scroll" ||
            style.overflowY === "auto" ||
            style.overflowX === "scroll" ||
            style.overflowX === "auto"
          ) {
            scrollableParents.push(parent);
          }
          parent = parent.parentElement;
        }

        return scrollableParents;
      };

      // Add scroll listeners to window and all scrollable parents
      const scrollableElements = [window];
      if (containerRef.current) {
        scrollableElements.push(...getScrollableParents(containerRef.current));
      }

      // Add listeners to all scrollable elements
      scrollableElements.forEach((element) => {
        element.addEventListener("scroll", handleScroll, { passive: true });
      });

      window.addEventListener("resize", handleResize);

      // Add class to body to prevent scrolling on mobile
      if (isMobile()) {
        document.body.style.overflow = "hidden";
      }

      return () => {
        // Remove listeners from all scrollable elements
        scrollableElements.forEach((element) => {
          element.removeEventListener("scroll", handleScroll);
        });

        window.removeEventListener("resize", handleResize);
        if (isMobile()) {
          document.body.style.overflow = "";
        }
      };
    }
  }, [openDateRanger]);

  const handleDateRangeChange = (item) => {
    const { startDate, endDate } = item.selection;
    setRangeState([item.selection]);
    onDateChange(
      format(startDate, "yyyy-MM-dd"),
      format(endDate, "yyyy-MM-dd")
    );
  };

  const clearDateRanger = () => {
    setOpenDateRanger(false);
    setRangeState(initialRangeState);
    onDateChange("", "");
  };

  return (
    <div style={{ position: "relative" }} ref={containerRef}>
      {openDateRanger && (
        <>
          {isMobile() && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 999998,
              }}
              onClick={() => setOpenDateRanger(false)}
            />
          )}
          <div
            className="openedDateRangerNew"
            style={{
              ...popupPosition,
              transition: isMobile()
                ? "none"
                : "opacity 0.2s ease, transform 0.2s ease",
            }}
          >
            <DateRanger
              handleDateRangeChange={handleDateRangeChange}
              rangeState={rangeState}
              onClose={() => setOpenDateRanger(false)}
              onClear={clearDateRanger}
            />
          </div>
        </>
      )}

      <div className="flex-k flex-col-k filterBlockDateRangerCustom">
        <div
          className={`commonWrapper ${
            startDate || endDate ? "has-selection" : ""
          }`}
        >
          {openDateRanger ? (
            <div className="closeAndClearDateRangerButtons">
              <button
                className="notmalButtonStyles noStyle closeRangerBtn"
                onClick={() => setOpenDateRanger(false)}
              >
                <CloseIcon /> Close
              </button>
              <button
                className="notmalButtonStyles noStyle clearRangerBtn"
                onClick={clearDateRanger}
              >
                <ClearIcon /> Clear
              </button>
            </div>
          ) : startDate || endDate ? (
            <div className="date-range-selected">
              <button
                className="notmalButtonStyles has-dates"
                onClick={handleOpenDateRanger}
              >
                {moment(startDate).format("DD-MM-YYYY")}
                <span>TO</span>
                {moment(endDate).format("DD-MM-YYYY")}
              </button>
              <button
                className="reset-button"
                onClick={clearDateRanger}
                style={{
                  color: "red",
                }}
              >
                <ClearIcon />
              </button>
            </div>
          ) : (
            <button
              className="notmalButtonStyles flexSpaceBetween"
              onClick={handleOpenDateRanger}
            >
              Select Date <CalenderIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DateRangerFilter;
